import { useEffect, useRef } from "react";
import { Ruler, X } from "lucide-react";
import { SIZE_CHART_ROWS } from "../data/sizing";

function MeasurementGuide() {
  return (
    <div className="measurement-guide" aria-label="Body measurement guide">
      <div className="measurement-guide-title">
        <span>Measurement guide</span>
        <small>Keep the tape comfortably close to the body</small>
      </div>
      <svg
        className="measurement-figure"
        viewBox="0 0 760 340"
        role="img"
        aria-labelledby="measurement-figure-title measurement-figure-desc"
      >
        <title id="measurement-figure-title">Front and back body measurement guide</title>
        <desc id="measurement-figure-desc">
          Illustration showing where to measure bust, waist, hips, shoulder and full length.
        </desc>
        <g className="body-outline" transform="translate(130 20)">
          <circle cx="100" cy="35" r="25" />
          <path d="M76 67 C58 78 48 113 50 150 L62 242 L82 242 L90 158 L110 158 L118 242 L138 242 L150 150 C152 113 142 78 124 67 C112 76 88 76 76 67 Z" />
          <path d="M55 91 L28 184 M145 91 L172 184" />
          <path className="measure-line" d="M52 109 H148" />
          <path className="measure-line" d="M58 139 H142" />
          <path className="measure-line" d="M54 169 H146" />
          <path className="measure-line vertical" d="M168 70 V241" />
          <text x="-4" y="113">BUST</text>
          <text x="0" y="143">WAIST</text>
          <text x="9" y="173">HIP</text>
          <text x="178" y="157">FULL LENGTH</text>
          <text className="figure-caption" x="100" y="282" textAnchor="middle">FRONT</text>
        </g>
        <g className="body-outline" transform="translate(455 20)">
          <circle cx="100" cy="35" r="25" />
          <path d="M76 67 C58 78 48 113 50 150 L62 242 L82 242 L90 158 L110 158 L118 242 L138 242 L150 150 C152 113 142 78 124 67 C112 76 88 76 76 67 Z" />
          <path d="M55 91 L28 184 M145 91 L172 184" />
          <path className="measure-line" d="M64 80 H136" />
          <path className="measure-line" d="M53 170 H147" />
          <path className="measure-line vertical" d="M100 70 V241" />
          <text x="139" y="84">SHOULDER</text>
          <text x="151" y="174">HIP</text>
          <text x="108" y="222">BACK LENGTH</text>
          <text className="figure-caption" x="100" y="282" textAnchor="middle">BACK</text>
        </g>
      </svg>
      <div className="measurement-notes">
        <div><strong>Bust</strong><span>Measure around the fullest part, keeping the tape level.</span></div>
        <div><strong>Waist</strong><span>Measure around your natural waist without pulling the tape tight.</span></div>
        <div><strong>Hip</strong><span>Measure around the fullest part of your hips with feet together.</span></div>
      </div>
    </div>
  );
}

export default function SizeChartModal({ open, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="size-chart-overlay" onMouseDown={onClose} data-testid="size-chart-overlay">
      <section
        className="size-chart-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="size-chart-title"
        onMouseDown={(event) => event.stopPropagation()}
        data-testid="size-chart-dialog"
      >
        <button
          ref={closeRef}
          type="button"
          className="size-chart-close"
          onClick={onClose}
          aria-label="Close size chart"
          data-testid="size-chart-close"
        >
          <X size={20} />
        </button>

        <header className="size-chart-header">
          <div className="size-chart-monogram" aria-hidden="true">LS</div>
          <div>
            <span className="eyebrow">Lalita Soujanya house sizing</span>
            <h2 id="size-chart-title">Size Chart</h2>
            <p><Ruler size={15} /> Measurements in inches</p>
          </div>
        </header>

        <div className="size-chart-layout">
          <div className="size-chart-sizing-column">
            <div className="size-chart-table-wrap">
              <table className="size-chart-table">
                <caption className="sr-only">Lalita Soujanya body measurements in inches</caption>
                <thead>
                  <tr>
                    <th scope="col">Size</th>
                    <th scope="col">UK</th>
                    <th scope="col">Bust</th>
                    <th scope="col">Waist</th>
                    <th scope="col">Hip</th>
                  </tr>
                </thead>
                <tbody>
                  {SIZE_CHART_ROWS.map((row) => (
                    <tr key={row.size}>
                      <th scope="row">{row.size}</th>
                      <td>{row.uk}</td>
                      <td>{row.bust}</td>
                      <td>{row.waist}</td>
                      <td>{row.hip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="custom-tailored-note">
              <span>Custom tailored</span>
              <p>Choose Custom Tailored and share your measurements with our studio on WhatsApp for a personalised fit.</p>
            </div>
          </div>

          <div className="size-chart-guide-column">
            <MeasurementGuide />
            <ul className="size-chart-tips">
              <li>Stand straight in a relaxed posture while taking body measurements.</li>
              <li>Avoid bulky clothing, and keep the measuring tape level without pulling it tight.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
